import { DeliveryTypeEnum } from "../types/DeliveryType";
import type { CheckoutShippingProfile, CheckoutShippingZonePrice } from "../models/CheckoutSettings";

type CartLineLite = {
  productId: string;
  quantity: number;
};

type ProductLean = {
  deliveryType?: string;
};

export function physicalProductIdsInCartLines({
  lineItemsInput,
  productsById,
}: {
  lineItemsInput: CartLineLite[];
  productsById: Map<string, ProductLean>;
}): string[] {
  const seen = new Set<string>();
  for (const line of lineItemsInput) {
    const prod = productsById.get(line.productId);
    if (!prod || line.quantity <= 0) continue;
    if (prod.deliveryType !== DeliveryTypeEnum.DIGITAL) {
      seen.add(line.productId);
    }
  }
  return [...seen];
}

/**
 * Uses the first zone whose `countries` includes the ship-to code (case-insensitive),
 * else the first zone with an empty `countries` array (default / rest of world).
 */
export function pricesForShippingProfileAndCountry({
  profile,
  countryCode,
}: {
  profile: CheckoutShippingProfile;
  countryCode: string | null | undefined;
}): CheckoutShippingZonePrice[] {
  const zones = profile.shippingZones ?? [];
  if (zones.length === 0) return [];

  const norm = countryCode?.trim().toUpperCase() ?? "";
  if (norm !== "") {
    for (const z of zones) {
      const codes = (z.countries ?? [])
        .map((c) => String(c).trim().toUpperCase())
        .filter(Boolean);
      if (codes.includes(norm)) {
        return z.prices ?? [];
      }
    }
  }

  const fallback = zones.find((z) => (z.countries ?? []).length === 0);
  if (fallback) {
    return fallback.prices ?? [];
  }

  return [];
}

/** Quote shipping from unique profiles touched by physical line items (one price per profile). */
export function getShippingForPhysicalCart({
  physicalProductIds,
  shippingProfiles,
  shipToCountryCode,
}: {
  physicalProductIds: string[];
  shippingProfiles: CheckoutShippingProfile[] | undefined | null;
  shipToCountryCode?: string | null;
}): { cents: number; label: string } {
  const zones = shippingProfiles ?? [];
  if (physicalProductIds.length === 0 || zones.length === 0) {
    return { cents: 0, label: "" };
  }

  const matchedProfileIndexes = new Set<number>();
  for (const pid of physicalProductIds) {
    const idx = zones.findIndex((z) =>
      (z.productIds ?? []).some((oid) => String(oid) === String(pid))
    );
    if (idx >= 0) matchedProfileIndexes.add(idx);
  }

  let cents = 0;
  const labelNames: string[] = [];
  const seenPriceNames = new Set<string>();
  const sorted = [...matchedProfileIndexes].sort((a, b) => a - b);
  for (const i of sorted) {
    const prof = zones[i];
    const tiers = pricesForShippingProfileAndCountry({
      profile: prof,
      countryCode: shipToCountryCode,
    });
    const option = tiers[0];
    if (!option || !Number.isFinite(option.price)) continue;
    cents += Math.max(0, Math.round(option.price));
    const priceName = String(option.name ?? "").trim();
    if (priceName && !seenPriceNames.has(priceName)) {
      seenPriceNames.add(priceName);
      labelNames.push(priceName);
    }
  }
  return {
    cents,
    label: labelNames.join("; "),
  };
}
